// Rutas maritimas aproximadas para dibujar el viaje en el mapa: un grafo
// pequeno de puntos en el mar (canales, estrechos, cruces oceanicos) y el
// camino mas corto entre el puerto de origen y el de destino. Es una
// estimacion para orientarse, no el rastreo de la naviera.

export type Coordenada = [number, number]; // [latitud, longitud]

const NODOS: Record<string, Coordenada> = {
  caribeColombia: [12.2, -74.5],
  caribeCentral: [15.5, -68],
  pasoAnegada: [18.6, -63.5],
  atlanticoCentral: [27, -45],
  atlanticoEste: [33.5, -14],
  gibraltar: [35.95, -5.6],
  mediterraneoOeste: [37.3, 2],
  canalSicilia: [36.7, 12.2],
  mediterraneoEste: [33.6, 27],
  portSaid: [31.3, 32.35],
  suez: [29.9, 32.6],
  marRojoNorte: [24.5, 35.8],
  marRojoSur: [16.5, 40.8],
  babElMandeb: [12.6, 43.4],
  golfoAden: [12.9, 50],
  marArabigo: [15.5, 62],
  costaOmani: [21, 60.5],
  golfoPersico: [25.7, 56.3],
  indiaOccidental: [20.5, 68.5],
  indiaSur: [7.5, 77.5],
  colombo: [6.6, 79.6],
  bengala: [7, 88],
  malaca: [4.4, 99.6],
  singapur: [1.2, 104.1],
  marChinaMeridional: [9, 111],
  estrechoLuzon: [20.5, 121],
  pacificoNoroeste: [30, 140],
  pacificoNorte: [38, 175],
  pacificoNoreste: [42, -140],
  pacificoMexico: [15, -104],
  pacificoPanama: [6.8, -80],
  canalPanama: [9.3, -79.9],
  pacificoSur: [-10, -82],
  indicoSur: [-14, 98],
  australiaOeste: [-31.5, 113],
  estrechoTorres: [-10.5, 142.6],
  marArafura: [-9, 134],
  marTimor: [-9.5, 124],
  javaSur: [-9, 112],
  bosforo: [41.15, 29.05],
  dardanelos: [40.1, 26.3],
  egeo: [37.2, 25.3],
  marNegro: [42.3, 28.5],
};

const ARISTAS: Array<[string, string]> = [
  ['caribeColombia', 'caribeCentral'],
  ['caribeCentral', 'pasoAnegada'],
  ['caribeCentral', 'canalPanama'],
  ['caribeColombia', 'canalPanama'],
  ['pasoAnegada', 'atlanticoCentral'],
  ['atlanticoCentral', 'atlanticoEste'],
  ['atlanticoEste', 'gibraltar'],
  ['gibraltar', 'mediterraneoOeste'],
  ['mediterraneoOeste', 'canalSicilia'],
  ['canalSicilia', 'mediterraneoEste'],
  ['mediterraneoEste', 'portSaid'],
  ['mediterraneoEste', 'egeo'],
  ['egeo', 'dardanelos'],
  ['dardanelos', 'bosforo'],
  ['bosforo', 'marNegro'],
  ['portSaid', 'suez'],
  ['suez', 'marRojoNorte'],
  ['marRojoNorte', 'marRojoSur'],
  ['marRojoSur', 'babElMandeb'],
  ['babElMandeb', 'golfoAden'],
  ['golfoAden', 'marArabigo'],
  ['marArabigo', 'costaOmani'],
  ['costaOmani', 'golfoPersico'],
  ['marArabigo', 'indiaOccidental'],
  ['costaOmani', 'indiaOccidental'],
  ['marArabigo', 'indiaSur'],
  ['indiaOccidental', 'indiaSur'],
  ['indiaSur', 'colombo'],
  ['colombo', 'bengala'],
  ['bengala', 'malaca'],
  ['malaca', 'singapur'],
  ['singapur', 'marChinaMeridional'],
  ['marChinaMeridional', 'estrechoLuzon'],
  ['estrechoLuzon', 'pacificoNoroeste'],
  ['pacificoNoroeste', 'pacificoNorte'],
  ['pacificoNorte', 'pacificoNoreste'],
  ['pacificoNoreste', 'pacificoMexico'],
  ['pacificoMexico', 'pacificoPanama'],
  ['pacificoPanama', 'canalPanama'],
  ['pacificoPanama', 'pacificoSur'],
  ['colombo', 'indicoSur'],
  ['indicoSur', 'australiaOeste'],
  ['indicoSur', 'javaSur'],
  ['javaSur', 'marTimor'],
  ['marTimor', 'marArafura'],
  ['marArafura', 'estrechoTorres'],
  ['singapur', 'javaSur'],
];

const RADIO_TIERRA_KM = 6371;
const grados = (v: number) => (v * Math.PI) / 180;

export function distanciaKm(a: Coordenada, b: Coordenada): number {
  const dLat = grados(b[0] - a[0]);
  const dLng = grados(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(grados(a[0])) * Math.cos(grados(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * RADIO_TIERRA_KM * Math.asin(Math.sqrt(h));
}

const vecinos = new Map<string, string[]>();
for (const [a, b] of ARISTAS) {
  vecinos.set(a, [...(vecinos.get(a) ?? []), b]);
  vecinos.set(b, [...(vecinos.get(b) ?? []), a]);
}

function nodoMasCercano(p: Coordenada): string {
  let mejor = '';
  let distancia = Infinity;
  for (const [nombre, c] of Object.entries(NODOS)) {
    const d = distanciaKm(p, c);
    if (d < distancia) {
      distancia = d;
      mejor = nombre;
    }
  }
  return mejor;
}

// Camino mas corto entre dos nodos del grafo (Dijkstra sobre pocos nodos).
function camino(desde: string, hasta: string): string[] {
  const dist = new Map<string, number>([[desde, 0]]);
  const previo = new Map<string, string>();
  const pendientes = new Set(Object.keys(NODOS));
  while (pendientes.size) {
    let actual: string | null = null;
    for (const n of pendientes) if (dist.has(n) && (actual === null || dist.get(n)! < dist.get(actual)!)) actual = n;
    if (actual === null) break;
    pendientes.delete(actual);
    if (actual === hasta) break;
    for (const v of vecinos.get(actual) ?? []) {
      const d = dist.get(actual)! + distanciaKm(NODOS[actual]!, NODOS[v]!);
      if (d < (dist.get(v) ?? Infinity)) {
        dist.set(v, d);
        previo.set(v, actual);
      }
    }
  }
  if (!dist.has(hasta)) return [desde, hasta];
  const ruta = [hasta];
  while (ruta[0] !== desde) ruta.unshift(previo.get(ruta[0]!)!);
  return ruta;
}

// Tramo entre dos puntos pasando por el mar.
function tramo(origen: Coordenada, destino: Coordenada): Coordenada[] {
  const a = nodoMasCercano(origen);
  const b = nodoMasCercano(destino);
  if (a === b) return [origen, destino];
  const nodos = camino(a, b).map(n => NODOS[n]!);
  // Si el primer nodo queda "detras" del origen respecto al destino, se omite (y lo mismo al final).
  const util = nodos.filter((n, i) => {
    if (i === 0) return distanciaKm(n, destino) < distanciaKm(origen, destino);
    if (i === nodos.length - 1) return distanciaKm(n, origen) < distanciaKm(destino, origen);
    return true;
  });
  return [origen, ...util, destino];
}

// Ruta completa: origen -> escalas informadas por la naviera -> destino.
export function rutaMaritima(origen: Coordenada, escalas: Coordenada[], destino: Coordenada): Coordenada[] {
  const paradas = [
    origen,
    ...escalas.filter(e => distanciaKm(e, origen) > 30 && distanciaKm(e, destino) > 30),
    destino,
  ];
  const ruta: Coordenada[] = [];
  for (let i = 0; i < paradas.length - 1; i++) {
    const parte = tramo(paradas[i]!, paradas[i + 1]!);
    ruta.push(...(i === 0 ? parte : parte.slice(1)));
  }
  return ruta;
}

// Punto de la ruta a una fraccion (0 a 1) de su longitud.
export function puntoEnRuta(ruta: Coordenada[], fraccion: number): Coordenada {
  if (ruta.length === 0) return [0, 0];
  if (ruta.length === 1) return ruta[0]!;
  const f = Math.max(0, Math.min(1, fraccion));
  const largos = ruta.slice(1).map((p, i) => distanciaKm(ruta[i]!, p));
  const total = largos.reduce((a, b) => a + b, 0);
  let objetivo = f * total;
  for (let i = 0; i < largos.length; i++) {
    const largo = largos[i]!;
    if (objetivo <= largo || i === largos.length - 1) {
      const t = largo === 0 ? 0 : Math.max(0, Math.min(1, objetivo / largo));
      const a = ruta[i]!;
      const b = ruta[i + 1]!;
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    objetivo -= largo;
  }
  return ruta[ruta.length - 1]!;
}
